import * as Yup from 'yup';
import React, { useState, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Grid, Hidden } from '@material-ui/core';
import { RelationshipsProps } from '../../models/account-info-epi-props';
import { Person } from '../../../../global/models/person-model';
import { RelationshipsState, Spouse } from '../relationships/relationships-model';
import { MaritalStatuses } from '../../../../global/constants/lists';
import { FormTypes, matchedPersonField } from '../../models/form-models';
import { MaritalStatusFormValues } from './marital-form-models';
import { MaritalFields } from './marital-fields';
import { getFormattedDate } from '../../../../global/helpers/date-helpers';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { ApiStatuses } from '../../../../global/models/api-model';
import { ConfirmationMarriedValidationSchema, MarriedValidationSchema, NotMarriedValidationSchema } from './marital-validation-schema';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { TcsmStates } from '../../../../root/tcsm-model';
import { DemographicAppKeys, DemographicsContext } from '../../../../global/models/demographics-model';
import { MaritalStatusMarriedFields } from './marital-status-married-fields';
import { MaritalStatusConfirmation } from './marital-status-confirmation';
import { PersonSearchResults } from '../../../arc/features/person-search/person-search-results';
import { PerformPersonSearchContext, PersonSearchContext, PersonSearchState, SearchPerson } from '../../../arc/features/person-search/person-search-model';
import { shouldContinueButtonBeDisabled, transformSearchPersonToMaritalFormValues } from '../../../arc/features/person-search/oscrr-helpers';
import { PersonSearchToastCombo } from '../../../arc/features/person-search/person-search-toast-combo';
import { getInitialEmails, getInitialMailingAddress, getInitialPersonInfo, getInitialPhones } from '../../helpers/form-init-helpers';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { FormBaseModel } from '../../../../global/models/page-model';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { PersonFieldsContentDisplay } from '../../components/read-only/person-fields-display';
import { MailingAddressDisplay } from '../../components/read-only/mailing-address-display';
import { PhonesDisplay } from '../../components/read-only/phones-display';
import { EmailsDisplay } from '../../components/read-only/emails-display';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { getGpsType } from '../../../../global/helpers/gps-helper';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../annual-enrollment/models/annual-enrollment-models';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { getCurrentFormattedDateNY } from '../../../../global/helpers/form-data-helper';
import { useSearchParams } from '../../../../global/hooks/useSearchParam';
import { noSelectPersonId } from '../../../arc/models/arc-epi-props';
import { FormField } from '../../../../global/components/form-element/form-field';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { NumberInputTypes } from '../../../../global/components/form-element/number-field';
import { GetAttestation1Label, GetAttestation2Label, getCurrentMaritalStatus } from './marital-status-helpers';

function getSuccessfulForms(relationshipsState: RelationshipsState, spousePersonSuccess: ApiStatuses, app: DemographicAppKeys, docUploaded?: boolean) {
	const maritalStatus = app === DemographicAppKeys.arc ? FormTypes.MAP_MARITAL_STATUS : FormTypes.MARITAL_STATUS;
	const maritalStatusSpouse = app === DemographicAppKeys.arc ? FormTypes.MAP_MARITAL_STATUS_SPOUSE : FormTypes.MARITAL_STATUS_SPOUSE;
	const docUploadSuccess = app === DemographicAppKeys.arc ? FormTypes.MAP_MARITAL_STATUS_DOC_UPLOAD_SUCCESS : FormTypes.MARITAL_STATUS_DOC_UPLOAD_SUCCESS;
	const successfulFormTypes = [];

	if (relationshipsState.singleStatus === ApiStatuses.Success) {
		successfulFormTypes.push(FormTypes.SINGLE_MARITAL_STATUS);
	}

	if (relationshipsState.patchSpouseStatus === ApiStatuses.Success
		|| relationshipsState.postSpouseStatus === ApiStatuses.Success
		|| relationshipsState.divorcedStatus === ApiStatuses.Success) {
		successfulFormTypes.push(docUploaded ? docUploadSuccess : maritalStatus);
	}

	if (relationshipsState.widowedStatus === ApiStatuses.Success) {
		successfulFormTypes.push(app === DemographicAppKeys.arc ? FormTypes.MAP_MARITAL_STATUS_WIDOW_SUCCESS : FormTypes.MARITAL_STATUS_WIDOW_SUCCESS);
	}

	if (relationshipsState.legalSeperationStats === ApiStatuses.Success) {
		successfulFormTypes.push(app === DemographicAppKeys.arc ? FormTypes.MAP_LEGAL_SEPERATION : FormTypes.LEGAL_SEPERATION);
	}

	if (spousePersonSuccess === ApiStatuses.Success) successfulFormTypes.push(docUploaded ? docUploadSuccess : maritalStatusSpouse);

	return successfulFormTypes;
}

interface MaritalStatusFormProps extends FormBaseModel {
	onSubmitHandler: (values: MaritalStatusFormValues, savedValues: MaritalStatusFormValues, formHelpers: FormikHelpers<MaritalStatusFormValues>, selectedPerson: SearchPerson) => any;
	currentPerson: Person;
	spouse: Spouse;
	relationshipsProps: RelationshipsProps;
	labelSetting: LabelSettingProps;
	previousRoute: string;
	showConfirmation: boolean;
	setShowConfirmation: (value: boolean) => void;
	skipDocUpload: boolean;
}

export function MaritalStatusForm(props: MaritalStatusFormProps) {
	const { currentPerson,
		onSubmitHandler,
		relationshipsProps,
		spouse,
		labelSetting,
		previousRoute,
		showConfirmation,
		setShowConfirmation,
		skipDocUpload,
	} = props;

	const groupName = matchedPersonField;
	const app = useContext(DemographicsContext);
	const enrollmentContext = useContext(AnnualEnrollmentContext);
	const isMember = app === DemographicAppKeys.myCPG;
	const isNewHireEnrollment = enrollmentContext === AnnualEnrollmentKey.newHireEnrollment;
	const isAnnualEnrollment = enrollmentContext === AnnualEnrollmentKey.annualEnrollment;
	const personSearchKey = useContext(PersonSearchContext);
	const personSearchState = useSelector<TcsmStates, PersonSearchState>(state => state.personSearchState[personSearchKey]);
	const relationshipState = useSelector<TcsmStates, RelationshipsState>(state => state.relationships[app]);
	const spouseIsClergy = spouse?.person?.cleric_in_book === 'Y' && currentPerson?.marital_status === MaritalStatuses.Married;
	const spousePersonSuccess = useSelector<TcsmStates, ApiStatuses>(state => state.personalInfo[app]?.patchPersonStatus);
	const history = useHistory();
	const searchResultsRef = useRef<HTMLDivElement>(null);
	const dispatch = useDispatch();
	const searchParams = useSearchParams();
	const apiErrors = useApiResponseError(FormTypes.MARITAL_STATUS);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps?.labelSettingState?.epiContent);
	const [selectedPerson, setSelectedPerson] = useState(null);
	let gpsItem = getGpsType(isAnnualEnrollment ? GpsType.AE_MARITAL_STATUS : GpsType.MARITAL_STATUS, app);
	const hasSearchResults: boolean = personSearchState?.searchResults?.length > 0;
	const plusSixtyFive = true;

	if (isNewHireEnrollment) {
		gpsItem = getGpsType(isMember ? GpsType.MEMBER_NEW_HIRE_MARITAL_STATUS : GpsType.NEW_HIRE_MARITAL_STATUS, app);
	}

	const isAdminSingleToMarried = (formMaritalStatus: MaritalStatuses) => {
		return app === DemographicAppKeys.arc
			&& (!currentPerson?.marital_status || currentPerson?.marital_status === MaritalStatuses.Single)
			&& formMaritalStatus === MaritalStatuses.Married;
	};

	const isAdminDivorceOrWidowedToMarried = (formMaritalStatus: MaritalStatuses) => {
		return app === DemographicAppKeys.arc
			&& (currentPerson?.marital_status === MaritalStatuses.Divorced || currentPerson?.marital_status === MaritalStatuses.Widowed)
			&& formMaritalStatus === MaritalStatuses.Married;
	};

	const isReportingSpouseDeath = (formMaritalStatus: MaritalStatuses) => {
		return formMaritalStatus === MaritalStatuses.Widowed;
	};

	const isReportingDivorce = (formMaritalStatus: MaritalStatuses) => {
		return formMaritalStatus === MaritalStatuses.Divorced;
	};

	const validationSchema = Yup.lazy(({
		marital_status,
		date_of_birth,
		matched_person,
		business_phone_formated,
		business_phone_country_code,
		mobile_phone_formated,
		mobile_phone_country_code,
		home_phone_formated,
		home_phone_country_code }) => {
		if (marital_status === MaritalStatuses.Single) return Yup.object().shape({});

		if (hasSearchResults && matched_person !== noSelectPersonId && marital_status === MaritalStatuses.Married && showConfirmation) {
			return Yup.object().shape(ConfirmationMarriedValidationSchema(labelSetting, relationshipsProps));
		}

		if (!hasSearchResults || (hasSearchResults && matched_person === noSelectPersonId)) {
			return marital_status === MaritalStatuses.Married
				? MarriedValidationSchema(labelSetting, relationshipsProps, spouseIsClergy, isAdminSingleToMarried(marital_status) || isAdminDivorceOrWidowedToMarried(marital_status), showConfirmation, date_of_birth,
					{
						business_phone_formated,
						business_phone_country_code,
						mobile_phone_formated,
						mobile_phone_country_code,
						home_phone_formated,
						home_phone_country_code,
					})
				: NotMarriedValidationSchema(labelSetting, relationshipsProps, isReportingSpouseDeath(marital_status), isReportingDivorce(marital_status));
		}

		return Yup.object().shape({});
	});

	const getRelationshipEndDate = () =>{
		if (currentPerson?.marital_status === MaritalStatuses.Widowed && spouse?.date_of_death){
			return getFormattedDate(spouse.date_of_death ?? spouse?.end_date);
		}

		return (spouse?.end_date || spouse?.legal_separation_date) ? getFormattedDate(spouse?.end_date || spouse?.legal_separation_date) : null;
	};



	const initialValues: MaritalStatusFormValues = {
		marital_status: getCurrentMaritalStatus(currentPerson, spouse),
		marital_status_effective_date: (spouse?.start_date && currentPerson.marital_status === MaritalStatuses.Married) ? getFormattedDate(spouse.start_date) : null,
		marriage_end_date: getRelationshipEndDate(),
		matched_person: null,
		attestation1: false,
		attestation2: false,
		court_issuing_decree: spouse?.court_issuing_decree,
		case_number: spouse?.case_number,


		...getInitialPersonInfo(currentPerson.marital_status === MaritalStatuses.Married ? spouse?.person : null),
		...getInitialMailingAddress(currentPerson.marital_status === MaritalStatuses.Married ? spouse?.person : null),
		...getInitialPhones(currentPerson.marital_status === MaritalStatuses.Married ? spouse?.person : null),
		...getInitialEmails(currentPerson.marital_status === MaritalStatuses.Married ? spouse?.person : null),

	};


	const onStatusChange = (formProps: FormikProps<MaritalStatusFormValues>) => {
		formProps.setErrors({});
		setShowConfirmation(false);
	};

	const [cachedFormValues, setCachedFormValues] = useState(initialValues);

	// on cancel, go back if on confirmation page. Also set values back to form if person was selected
	const onCancel = (formProps: FormikProps<MaritalStatusFormValues>) => {
		if (showConfirmation) {
			formProps.setValues(cachedFormValues);
			formProps.setFieldValue(groupName, null);
			formProps.setTouched({});
			setShowConfirmation(false);
			setSelectedPerson(null);
		} else {
			history.push(previousRoute);
		}
	};

	const addressExpired = getFormattedDate(spouse?.person?.mailing_address_expiration_date) < getCurrentFormattedDateNY();
	const [addressExpiredError, setAddressExpiredError] = useState(addressExpired);

	const renderFormContent = (formProps: FormikProps<MaritalStatusFormValues>) => { //
		if (formProps.values.marital_status === MaritalStatuses.Divorced) {
			return (
				<>
					<ContentSpacer>
						<FormField
							name='court_issuing_decree'
							errors={formProps.errors}
							touched={formProps.touched}
							required
							label={relationshipsProps.courtIssuingDecreeLabel}
							tooltip={{ title: relationshipsProps.courtIssuingDecreeLabelTooltip }}
							autoWidth
						/>
					</ContentSpacer>
					<ContentSpacer>
						<FormField
							type={NumberInputTypes.Integer}
							name='case_number'
							errors={formProps.errors}
							touched={formProps.touched}
							label={relationshipsProps.caseNumberLabel}
							required
							autoWidth
							tooltip={{ title: relationshipsProps.caseNumberTooltip }}
						/>
					</ContentSpacer>
				</>
			);
		}
		
		if (formProps.values.marital_status === MaritalStatuses.Widowed || (formProps.values.marital_status === MaritalStatuses.Married && !!spouse?.legal_separation_date) || formProps.values.marital_status === MaritalStatuses.Divorced || formProps.values.marital_status === MaritalStatuses.LegallySeparated) {
			if (initialValues?.marital_status === MaritalStatuses.Widowed || (initialValues?.marital_status === MaritalStatuses.Married && !!spouse?.legal_separation_date) || initialValues?.marital_status === MaritalStatuses.Divorced) {
				return (
					<>{
						spouse?.person?.first_name &&
						<DividerSection removeDivider sectionText={relationshipsProps.spouseInformationSectionLabel}>
							<PersonFieldsContentDisplay
								labelSetting={labelSettings}
								values={getInitialPersonInfo(spouse?.person)}
								dobMasked
							/>
						</DividerSection>
					}
						{
							(spouse?.person?.mailing_address_1 || spouse?.person?.mailing_address_2 || spouse?.person?.mailing_address_3) &&
							<MailingAddressDisplay
								labelSetting={labelSettings}
								values={getInitialMailingAddress(spouse?.person)}
							/>
						}
						{
							(spouse?.person?.business_phone_number || spouse?.person?.home_phone_number) &&
							<DividerSection removeDivider>
								<PhonesDisplay
									labelSetting={labelSettings}
									values={getInitialPhones(spouse?.person)}
								/>
							</DividerSection>
						}
						{
							(spouse?.person?.personal_email_address || spouse?.person?.office_email_address) &&
							<DividerSection removeDivider>
								<EmailsDisplay
									labelSetting={labelSettings}
									values={getInitialEmails(spouse?.person)}
								/>
							</DividerSection>
						}
					</>);
			}
			return null;
		}

		const adminSingleToMarried = isAdminSingleToMarried(formProps.values.marital_status);
		const adminDivorceOrWidowedToMarried = isAdminDivorceOrWidowedToMarried(formProps.values.marital_status);

		if ((adminSingleToMarried || adminDivorceOrWidowedToMarried) && showConfirmation) {
			return (
				<MaritalStatusConfirmation
					labelSetting={labelSetting}
					relationshipsProps={relationshipsProps}
					formProps={formProps}
					personSelected={!!selectedPerson}
				/>
			);
		}

		if (formProps.values.marital_status === MaritalStatuses.Single) {
			return null;
		}

		return (
			<PerformPersonSearchContext.Provider value={adminSingleToMarried || adminDivorceOrWidowedToMarried}>
				<MaritalStatusMarriedFields
					labelSetting={labelSetting}
					relationshipsProps={relationshipsProps}
					formProps={formProps}
					spouseIsClergy={spouseIsClergy}
					addressExpired={addressExpired}
					addressExpiredError={addressExpiredError}
					setAddressExpiredError={setAddressExpiredError}
					docUpload={!!spouse && !skipDocUpload}
					currentPerson={spouse?.person}
					plusSixtyFive={plusSixtyFive}
				/>
			</PerformPersonSearchContext.Provider>
		);
	};

	const getButtonText = (formMaritalStatus: MaritalStatuses) => {
		if (isAdminSingleToMarried(formMaritalStatus) || isAdminDivorceOrWidowedToMarried(formMaritalStatus)) {
			return showConfirmation ? [labelSetting.submitText, labelSetting.goBackText] : [labelSetting.continueText, labelSetting.cancelGoBackText];
		}
		return [labelSetting.submitText, labelSetting.cancelGoBackText];
	};

	const onNavigateToTop = () => {
		window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
	};

	const onPersonSelect = (person: SearchPerson, formProps: FormikProps<MaritalStatusFormValues>, values: MaritalStatusFormValues) => {
		dispatch(apiErrorClear());
		setSelectedPerson(person);
		setCachedFormValues(values);
		formProps.setValues(transformSearchPersonToMaritalFormValues(person, values.marital_status));
		formProps.setTouched({});
		setShowConfirmation(true);
		onNavigateToTop();
	};

	const hideDocUpload = (formProps: FormikProps<MaritalStatusFormValues>) => {
		return ((currentPerson?.marital_status === MaritalStatuses.Married && formProps?.values?.marital_status === MaritalStatuses.Married) && !spouse && !showConfirmation) ||
			(app === DemographicAppKeys.arc && !showConfirmation && currentPerson?.marital_status === MaritalStatuses.Single);
	};

	function onInternalSubmit(values: MaritalStatusFormValues, savedValues: MaritalStatusFormValues, formHelpers: FormikHelpers<MaritalStatusFormValues>, selectedPersonItem: SearchPerson) {

		const formProps: FormikProps<MaritalStatusFormValues> = formHelpers as FormikProps<MaritalStatusFormValues>;

		if (values) {
			const selectedPersonId = values[groupName];

			if (selectedPersonId &&
				selectedPersonId !== noSelectPersonId &&
				selectedPersonId !== selectedPerson?.guid &&
				personSearchState?.searchResults?.length > 0) {

				const selectedPersonGrid = personSearchState?.searchResults?.find(employee => employee.guid === selectedPersonId);

				if (selectedPersonGrid) {
					onPersonSelect(selectedPersonGrid, formProps, values);
					return;
				}
			}
		}

		onSubmitHandler(values, savedValues, formHelpers, selectedPersonItem);
	}


	function renderAttestations(formProps: FormikProps<MaritalStatusFormValues>) {
		if (app === DemographicAppKeys.arc && (isAdminSingleToMarried(formProps?.values.marital_status) || isAdminDivorceOrWidowedToMarried(formProps?.values.marital_status)) && !showConfirmation) return null;

		return (
			<>
				<div data-formdata='attestation1'>
					<FormField
						name='attestation1'
						label={GetAttestation1Label(app, relationshipsProps)}
						component='checkBox'
						touched={formProps.touched}
						errors={formProps.errors}
						value={formProps?.values?.attestation1 ?? false}
						checked={formProps?.values?.attestation1 ?? false}
						required
					/>
				</div>
				{
					!isReportingSpouseDeath(formProps?.values.marital_status) &&
					<div data-formdata='attestation2'>
						<FormField
							data-formdata='attestation2'
							name='attestation2'
							label={GetAttestation2Label(app, relationshipsProps)}
							component='checkBox'
							touched={formProps.touched}
							errors={formProps.errors}
							value={formProps?.values?.attestation2 ?? false}
							checked={formProps?.values?.attestation2 ?? false}
							required
						/>
					</div>
				}
			</>
		);
	}

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values, formikHelpers) => onInternalSubmit(values, initialValues, formikHelpers, selectedPerson)}
			validationSchema={validationSchema}
			enableReinitialize
			initialErrors={apiErrors}
		>
			{(formProps: FormikProps<MaritalStatusFormValues>) => (
				<>
					<Form>
						<Grid>
							<FormWrapper
								formikProps={formProps}
								submitText={getButtonText(formProps.values.marital_status)[0]}
								cancelText={getButtonText(formProps.values.marital_status)[1]}
								onCancel={() => onCancel(formProps)}
								successfulForms={getSuccessfulForms(relationshipState, spousePersonSuccess, app, !!searchParams?.get('docUpload'))}
								submitDisabled={addressExpiredError || shouldContinueButtonBeDisabled(formProps.values.matched_person, personSearchState?.searchResults)}
								additionalContentAboveButtons={formProps?.values?.marital_status !== MaritalStatuses.Single &&
									renderAttestations(formProps)}
								personSearchToast={
									<PersonSearchToastCombo
										searchResultsRef={searchResultsRef}
										renderResultsMessaging={(isAdminSingleToMarried(formProps.values.marital_status) || isAdminDivorceOrWidowedToMarried(formProps.values.marital_status)) && !showConfirmation && personSearchState.searchResults?.length > 0}
									/>
								}
								formType={initialValues?.marital_status !== formProps?.values?.marital_status ? FormTypes.MARITAL_STATUS : FormTypes.MARITAL_STATUS_SPOUSE}
								defaultShowDocUpload={isAdminSingleToMarried(formProps.values.marital_status) || initialValues?.marital_status !== formProps?.values?.marital_status}
								formKeys={Object.keys(initialValues)}
								personSearchResults={
									(isAdminSingleToMarried(formProps.values.marital_status) || isAdminDivorceOrWidowedToMarried(formProps.values.marital_status)) && !showConfirmation &&
									<PersonSearchResults
										groupName={groupName}
										backToTopAction={onNavigateToTop}
										results={personSearchState?.searchResults}
										resultsRef={searchResultsRef} />
								}
								hideDocUpload={hideDocUpload(formProps)}
							>
								<PageHeader
									headerText={props.formHeader}
									description={props.formDescription}
								/>
								<Hidden mdUp>
									<SideBarContent>
										<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
									</SideBarContent>
								</Hidden>
								<MaritalFields
									formProps={formProps}
									maritalStatusLabel={relationshipsProps.maritalStatusLabel}
									dateOfMarriageLabel={relationshipsProps.dateOfMarriageLabel}
									endOfMarriageLabel={relationshipsProps.endOfMarriageLabel}
									tooltipMessage={relationshipsProps.dateOfMarriageTooltip}
									maritalStatus={currentPerson?.marital_status}
									effectiveDate={spouse?.start_date}
									admin={app === DemographicAppKeys.arc}
									onStatusChange={() => onStatusChange(formProps)}
									showConfirmation={showConfirmation}
									widowedPreliminaryDateDeathLabel={relationshipsProps.widowedPreliminaryDateDeathLabel}
									widowedDateDeathLabel={relationshipsProps.widowedDateDeathLabel}
									legallySeparatedLabel={relationshipsProps.legallySeparationDatelabel}
									legalSeparationDate={spouse?.legal_separation_date}
									relationshipsProps={relationshipsProps}
								/>
								{renderFormContent(formProps)}


							</FormWrapper>
						</Grid>
					</Form>
				</>
			)}

		</Formik>
	);
}

